import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Search, Plus, Pencil, Trash2, MapPin, Phone, Mail, Globe, Clock,
  Image as ImageIcon, GripVertical, Save, X as XIcon, Upload, Loader2,
  FileText, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const empty = () => ({
  name: '', address: '', phone: '', email: '', website: '',
  default_duration_min: '', notes: '', photo_b64: '', photo_name: '',
});

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = 'image/*,application/pdf';

const isPdfDataUrl = (s) => typeof s === 'string' && s.startsWith('data:application/pdf');
const isImageDataUrl = (s) => typeof s === 'string' && s.startsWith('data:image/');

/**
 * Full database view for the Activity Bank.
 * Replaces the cluttered chip wall. Each entry stores address, phone, email,
 * website, default duration, notes, and an optional photo — when dragged onto
 * a group, those fields auto-populate the activity dialog.
 */
export default function BankManagerDialog({
  open, onClose, bank = [], isScheduler, api, onChanged, onAssign, onDragStart, onDragEnd,
}) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | item object
  const [draft, setDraft] = useState(empty());
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bank;
    return bank.filter(b => (
      (b.name || '').toLowerCase().includes(q) ||
      (b.address || '').toLowerCase().includes(q) ||
      (b.notes || '').toLowerCase().includes(q)
    ));
  }, [bank, search]);

  const startNew = () => { setDraft(empty()); setEditing('new'); };
  const startEdit = (item) => { setDraft({ ...empty(), ...item }); setEditing(item); };
  const cancelEdit = () => { setEditing(null); setDraft(empty()); };

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) { toast.error(`File too large (max ${MAX_UPLOAD_BYTES / 1024 / 1024} MB)`); e.target.value = ''; return; }
    const isPdf = f.type === 'application/pdf';
    const isImg = f.type.startsWith('image/');
    if (!isPdf && !isImg) { toast.error('Only images or PDFs are allowed'); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = () => setDraft(p => ({ ...p, photo_b64: reader.result, photo_name: f.name }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    const name = draft.name.trim();
    if (!name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        address: draft.address || '',
        phone: draft.phone || '',
        email: draft.email || '',
        website: draft.website || '',
        default_duration_min: draft.default_duration_min ? parseInt(draft.default_duration_min, 10) || null : null,
        notes: draft.notes || '',
        photo_b64: draft.photo_b64 || '',
        photo_name: draft.photo_name || '',
      };
      if (editing === 'new') {
        await api.post('/activity-bank', payload);
        toast.success('Added to bank');
      } else {
        await api.put(`/activity-bank/${editing.id}`, payload);
        toast.success('Updated');
      }
      cancelEdit();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the bank? Existing scheduled activities won't be affected.`)) return;
    try {
      await api.delete(`/activity-bank/${item.id}`);
      toast.success('Deleted');
      onChanged?.();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl bg-white p-0" data-testid="bank-manager-dialog">
        <div className="p-5 border-b border-[#DED8EB] flex items-center justify-between flex-wrap gap-2">
          <div>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#2D1B4E]">Activity Bank</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[#8E85A0]">Your saved places — locations, contacts, defaults. Drag from here onto a group, or click an entry to assign it.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#8E85A0]" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, address, notes…"
                className="border-[#DED8EB] h-8 text-xs pl-7 w-64"
                data-testid="bank-manager-search"
              />
            </div>
            {isScheduler && editing === null && (
              <Button size="sm" onClick={startNew} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8 text-xs gap-1" data-testid="bank-manager-add-btn">
                <Plus className="h-3 w-3" /> Add Entry
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto p-5">
          {editing !== null && (
            <EntryEditor
              draft={draft}
              setDraft={setDraft}
              onSave={save}
              onCancel={cancelEdit}
              onPickPhoto={onPickPhoto}
              saving={saving}
              isNew={editing === 'new'}
            />
          )}

          {editing === null && filtered.length === 0 && (
            <div className="text-center py-12 text-[#8E85A0] text-sm" data-testid="bank-empty-state">
              {bank.length === 0 ? (
                <>
                  <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p>No bank entries yet.</p>
                  {isScheduler && <p className="text-xs mt-1">Click <strong>Add Entry</strong> to save the first place.</p>}
                </>
              ) : <p>No entries match &ldquo;{search}&rdquo;.</p>}
            </div>
          )}

          {editing === null && filtered.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="bank-entries-grid">
              {filtered.map(item => (
                <BankEntryCard
                  key={item.id}
                  item={item}
                  isScheduler={isScheduler}
                  onAssign={() => onAssign?.(item)}
                  onEdit={() => startEdit(item)}
                  onDelete={() => remove(item)}
                  onDragStart={(e) => {
                    if (!isScheduler) return;
                    e.dataTransfer.setData('text/plain', item.name);
                    e.dataTransfer.effectAllowed = 'copy';
                    onDragStart?.(item);
                    // Close the manager after the drag starts so the user can see/drop on group cards
                    setTimeout(() => onClose?.(), 50);
                  }}
                  onDragEnd={() => onDragEnd?.()}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BankEntryCard({ item, isScheduler, onAssign, onEdit, onDelete, onDragStart, onDragEnd }) {
  return (
    <div
      draggable={isScheduler}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`border border-[#DED8EB] rounded-lg bg-white hover:border-[#6B3FA0] hover:shadow-md transition-all overflow-hidden ${isScheduler ? 'cursor-grab active:cursor-grabbing' : ''}`}
      data-testid={`bank-entry-${item.id}`}>
      {isImageDataUrl(item.photo_b64) && (
        <img src={item.photo_b64} alt={item.name} className="w-full h-28 object-cover" />
      )}
      {isPdfDataUrl(item.photo_b64) && (
        <a href={item.photo_b64}
           download={item.photo_name || `${item.name}.pdf`}
           target="_blank" rel="noreferrer"
           onClick={e => e.stopPropagation()}
           className="flex items-center gap-2 p-2 bg-[#FDF6EC] border-b border-[#F0E4C8] text-[11px] text-[#8B5A00] hover:bg-[#F9EDD6] transition-colors"
           data-testid={`bank-entry-pdf-${item.id}`}
           title={`Open ${item.photo_name || 'PDF'}`}>
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate flex-1">{item.photo_name || 'PDF attachment'}</span>
          <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
        </a>
      )}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {isScheduler && <GripVertical className="h-3 w-3 text-[#9B7ED8] shrink-0" />}
            <h4 className="text-sm font-bold text-[#2D1B4E] truncate" title={item.name}>{item.name}</h4>
          </div>
          {isScheduler && (
            <div className="flex items-center gap-0.5 shrink-0">
              <button onClick={onEdit} className="text-[#8E85A0] hover:text-[#6B3FA0] p-1" title="Edit" data-testid={`bank-edit-${item.id}`}>
                <Pencil className="h-3 w-3" />
              </button>
              <button onClick={onDelete} className="text-[#8E85A0] hover:text-[#D9534F] p-1" title="Delete" data-testid={`bank-delete-${item.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        <div className="space-y-1 text-[11px] text-[#5A4D6E]">
          {item.address && <DetailRow Icon={MapPin} text={item.address} />}
          {item.phone && <DetailRow Icon={Phone} text={item.phone} href={`tel:${item.phone}`} />}
          {item.email && <DetailRow Icon={Mail} text={item.email} href={`mailto:${item.email}`} />}
          {item.website && <DetailRow Icon={Globe} text={item.website.replace(/^https?:\/\//, '')} href={item.website.startsWith('http') ? item.website : `https://${item.website}`} external />}
          {item.default_duration_min && <DetailRow Icon={Clock} text={`Default ${item.default_duration_min} min`} />}
        </div>
        {item.notes && (
          <p className="text-[11px] text-[#8E85A0] mt-2 line-clamp-3 italic" title={item.notes}>{item.notes}</p>
        )}
        {isScheduler && (
          <Button
            size="sm"
            variant="outline"
            onClick={onAssign}
            className="w-full mt-2 h-7 text-[11px] border-[#DED8EB] text-[#6B3FA0] hover:bg-[#F0EDF6]"
            data-testid={`bank-assign-${item.id}`}>
            Assign to a group →
          </Button>
        )}
      </div>
    </div>
  );
}

function DetailRow({ Icon, text, href, external }) {
  const content = (
    <span className="flex items-start gap-1.5 truncate">
      <Icon className="h-3 w-3 text-[#9B7ED8] mt-0.5 shrink-0" />
      <span className="truncate">{text}</span>
    </span>
  );
  if (href) {
    return (
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} onClick={e => e.stopPropagation()} className="hover:text-[#6B3FA0] hover:underline block">
        {content}
      </a>
    );
  }
  return content;
}

function EntryEditor({ draft, setDraft, onSave, onCancel, onPickPhoto, saving, isNew }) {
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  return (
    <div className="border border-[#DED8EB] rounded-lg p-4 bg-[#FBFAFE] mb-4" data-testid="bank-entry-editor">
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#DED8EB]">
        <h3 className="text-sm font-bold text-[#2D1B4E]">{isNew ? 'Add Bank Entry' : `Edit "${draft.name || 'entry'}"`}</h3>
        <button onClick={onCancel} disabled={saving} className="text-[#8E85A0] hover:text-[#2D1B4E]" data-testid="bank-editor-close">
          <XIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-xs text-[#5A4D6E]">Name *</Label>
          <Input value={draft.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Trenton YMCA" className="border-[#DED8EB] h-8 text-sm mt-1" data-testid="bank-editor-name" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-[#5A4D6E]">Address</Label>
          <Input value={draft.address} onChange={e => set('address', e.target.value)} placeholder="431 Pennington Rd, Trenton NJ" className="border-[#DED8EB] h-8 text-sm mt-1" data-testid="bank-editor-address" />
        </div>
        <div>
          <Label className="text-xs text-[#5A4D6E]">Phone</Label>
          <Input value={draft.phone} onChange={e => set('phone', e.target.value)} placeholder="(609) 555-0123" className="border-[#DED8EB] h-8 text-sm mt-1" data-testid="bank-editor-phone" />
        </div>
        <div>
          <Label className="text-xs text-[#5A4D6E]">Email</Label>
          <Input value={draft.email} onChange={e => set('email', e.target.value)} placeholder="contact@ymca.org" className="border-[#DED8EB] h-8 text-sm mt-1" data-testid="bank-editor-email" />
        </div>
        <div>
          <Label className="text-xs text-[#5A4D6E]">Website</Label>
          <Input value={draft.website} onChange={e => set('website', e.target.value)} placeholder="https://ymca.org" className="border-[#DED8EB] h-8 text-sm mt-1" data-testid="bank-editor-website" />
        </div>
        <div>
          <Label className="text-xs text-[#5A4D6E]">Default Duration (minutes)</Label>
          <Input
            type="number"
            min="15"
            max="480"
            step="15"
            value={draft.default_duration_min || ''}
            onChange={e => set('default_duration_min', e.target.value)}
            placeholder="90"
            className="border-[#DED8EB] h-8 text-sm mt-1"
            data-testid="bank-editor-duration"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-[#5A4D6E]">Notes</Label>
          <Textarea
            value={draft.notes}
            onChange={e => set('notes', e.target.value)}
            placeholder="Bring water bottles. Park on the west side. Mike is the regular contact on weekends."
            className="border-[#DED8EB] text-sm mt-1 min-h-[64px]"
            data-testid="bank-editor-notes"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-[#5A4D6E]">Photo or PDF (optional, ≤10MB)</Label>
          <div className="flex items-center gap-3 mt-1">
            {isImageDataUrl(draft.photo_b64) && (
              <div className="relative">
                <img src={draft.photo_b64} alt="" className="h-16 w-24 object-cover rounded-md border border-[#DED8EB]" />
                <button onClick={() => { set('photo_b64', ''); set('photo_name', ''); }} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-[#DED8EB] text-[#8E85A0] hover:text-[#D9534F] flex items-center justify-center" data-testid="bank-editor-clear-photo">
                  <XIcon className="h-2.5 w-2.5" />
                </button>
              </div>
            )}
            {isPdfDataUrl(draft.photo_b64) && (
              <div className="relative h-16 w-24 rounded-md border border-[#DED8EB] bg-[#FDF6EC] flex flex-col items-center justify-center gap-1 px-2">
                <FileText className="h-5 w-5 text-[#8B5A00]" />
                <span className="text-[9px] text-[#8B5A00] truncate w-full text-center" title={draft.photo_name}>{draft.photo_name || 'PDF'}</span>
                <button onClick={() => { set('photo_b64', ''); set('photo_name', ''); }} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-[#DED8EB] text-[#8E85A0] hover:text-[#D9534F] flex items-center justify-center" data-testid="bank-editor-clear-photo">
                  <XIcon className="h-2.5 w-2.5" />
                </button>
              </div>
            )}
            {!draft.photo_b64 && (
              <div className="h-16 w-24 rounded-md border-2 border-dashed border-[#DED8EB] flex items-center justify-center text-[#9B7ED8] gap-1">
                <ImageIcon className="h-4 w-4" />
                <FileText className="h-4 w-4" />
              </div>
            )}
            <label className="text-xs px-2.5 py-1 rounded border border-[#DED8EB] hover:border-[#6B3FA0] hover:text-[#6B3FA0] cursor-pointer flex items-center gap-1 inline-flex w-fit" data-testid="bank-editor-photo-upload">
              <Upload className="h-3 w-3" /> {draft.photo_b64 ? 'Replace' : 'Upload photo or PDF'}
              <input type="file" accept={ACCEPTED_TYPES} className="hidden" onChange={onPickPhoto} />
            </label>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[#DED8EB]">
        <Button variant="outline" onClick={onCancel} disabled={saving} className="border-[#DED8EB] h-8 text-xs">Cancel</Button>
        <Button onClick={onSave} disabled={saving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8 text-xs gap-1" data-testid="bank-editor-save">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          {saving ? 'Saving…' : (isNew ? 'Add to Bank' : 'Save Changes')}
        </Button>
      </div>
    </div>
  );
}
